<template>
  <div id="ip-tools">
    <h1>{{ $t('system.ip_tools') }}</h1>
    <p>{{ $t('views.tools.ip_tools.note') }}</p>
    <b-card :title="$t('views.tools.ip_tools.expand_compress_address')">
      <b-form-group :label="$t('system.ipv6_address')">
        <b-form-input v-model="ip_expand_compress" :placeholder="$t('system.ipv6_address')"
                      :state="checkIPExpandCompress" required></b-form-input>
        <b-form-invalid-feedback id="input-live-feedback-expand">
          {{ $t('system.invalid_ipv6') }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="$t('views.tools.ip_tools.expanded_address')">
        <CopyField :text="expandIP" code/>
      </b-form-group>
      <b-form-group :label="$t('views.tools.ip_tools.compressed_address')">
        <CopyField :text="compressIP" code/>
      </b-form-group>
    </b-card>
    <br>
    <b-card :title="$t('views.tools.ip_tools.subnet_calculator')">
      <b-form-group :label="$t('views.tools.ip_tools.ipv6_subnet')">
        <b-form-input v-model="subnet" :placeholder="$t('views.tools.ip_tools.ipv6_subnet')"
                      :state="checkIPSubnet" required></b-form-input>
        <b-form-invalid-feedback id="input-live-feedback-compress">
          {{ $t('views.tools.ip_tools.invalid_ipv6_subnet') }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="$t('system.start')">
        <CopyField :text="calculateRange.start" code/>
      </b-form-group>
      <b-form-group :label="$t('system.end')">
        <CopyField :text="calculateRange.end" code/>
      </b-form-group>
    </b-card>
    <br>
    <b-card :title="$t('views.tools.ip_tools.mac_to_eui')">
      <b-form-group :label="$t('system.mac_address')">
        <b-form-input v-model="mac_to_local_link" :placeholder="$t('system.mac_address')"
                      :state="checkMacAddress" required></b-form-input>
        <b-form-invalid-feedback id="input-live-feedback-compress">
          {{ $t('views.tools.ip_tools.invalid_mac_address') }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="$t('views.tools.ip_tools.prefix')">
        <b-form-select v-model="selectedPrefix">
          <b-form-select-option-group v-for="subnet in prefix_list" :key="subnet.name" :label="subnet.name">
            <b-form-select-option v-for="prefix in subnet.prefixes" :key="prefix.value" :value="prefix.value">{{ prefix.label }}</b-form-select-option>
          </b-form-select-option-group>
        </b-form-select>
      </b-form-group>
      <b-form-group :label="$t('views.tools.ip_tools.eui64_address')">
        <CopyField :text="eui" code/>
      </b-form-group>
    </b-card>
    <br>
    <b-card :title="$t('views.tools.ip_tools.ipv6_to_mac')">
      <b-form-group :label="$t('system.ipv6_address')">
        <b-form-input v-model="local_link_to_mac" :placeholder="$t('system.ipv6_address')"
                      :state="checkEUIIPv6Address" required></b-form-input>
        <b-form-invalid-feedback id="input-live-feedback-compress">
          <span v-if="checkEUI_IPv6">
            {{ $t('views.tools.ip_tools.ipv6_no_eui') }}
          </span>
          <span v-else>
            {{ $t('system.invalid_ipv6') }}
          </span>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="$t('system.mac_address')">
        <CopyField :text="macAddress" code/>
      </b-form-group>
    </b-card>
  </div>
</template>

<script>
import CopyField from '@/components/CopyField.vue'
import ipaddress from '@/util/ipaddress'
import Dns_bcdService from '@/api-services/dns_bcd.service'

export default {
  name: 'IPv6',
  components: { CopyField },
  computed: {
    expandIP() {
      return ipaddress.normalize_ipv6(this.ip_expand_compress)
    },
    compressIP() {
      return ipaddress.abbreviate_ipv6(this.ip_expand_compress)
    },
    calculateRange() {
      if (ipaddress.check_ipv6_subnet(this.subnet)) {
        const split = this.subnet.split('/')
        return ipaddress.ipv6_range(split[0], split[1], 128)
      } else {
        return {
          start: '',
          end: '',
          size: 0
        }
      }
    },
    eui() {
      return ipaddress.mac_to_eui_ipv6(this.mac_to_local_link, this.selectedPrefix)
    },
    macAddress() {
      return ipaddress.eui_ipv6_to_mac(this.local_link_to_mac)
    },
    checkIPExpandCompress() {
      if (!this.ip_expand_compress) {
        return null
      }
      return ipaddress.is_ip_v6(this.ip_expand_compress)
    },
    checkIPSubnet() {
      if (!this.subnet) {
        return null
      }
      return ipaddress.check_ipv6_subnet(this.subnet)
    },
    checkMacAddress() {
      if (!this.mac_to_local_link) {
        return null
      }
      return ipaddress.is_mac_address(this.mac_to_local_link)
    },
    checkEUIIPv6Address() {
      if (!this.local_link_to_mac) {
        return null
      }
      return (ipaddress.is_ip_v6(this.local_link_to_mac) && ipaddress.is_eui_ipv6(this.local_link_to_mac))
    },
    checkEUI_IPv6() {
      if (!this.local_link_to_mac) {
        return null
      }
      return ipaddress.is_ip_v6(this.local_link_to_mac)
    }
  },
  async mounted() {
    if (this.$store.state.user) {
      console.log(this.$store.state.user)
      const res = (await Dns_bcdService.getOverview(this.$store.state)).data

      for (const a of res.bcd2ou_list) {
        // Check if the OU is already defined
        const subnet = this.prefix_list.find(element => element.name === a.ou_fq_name)

        if (subnet === undefined) {
          // Add missing OUs to array
          this.prefix_list.push({
            name: a.ou_fq_name,
            bcd: [a.bcd_name],
            prefixes: [],
          })
        } else {
          subnet.bcd.push(a.bcd_name)
        }
      }

      // Look over the subnets
      res.ip_subnet_list
        .filter(subnet => subnet.type === '6')
        .filter(subnet => ipaddress.get_ipv6_prefix_length(subnet.cidr) <= 64)
        .forEach((subnet) => {
          const a = this.prefix_list.find(element => element.bcd.includes(subnet.bcd))
          if (a !== undefined) {
            a.prefixes.push({
              label: `${subnet.bcd} (${subnet.cidr})`,
              value: subnet.cidr,
            })
          }
        })
    }
  },
  data() {
    return {
      ip_expand_compress: '',
      subnet: '',
      mac_to_local_link: '',
      local_link_to_mac: '',
      selectedPrefix: 'fe80::/10',
      prefix_list: [
        {
          name: this.$t('views.tools.ip_tools.link_local'),
          bcd: [],
          prefixes: [
            {
              label: this.$t('views.tools.ip_tools.link_local') + ' (fe80::/10)',
              value: 'fe80::/10',
            },
          ],
        }
      ],
    }
  }
}
</script>

<style scoped>

</style>
